import { LOCAL_STORAGE_KEY } from 'constants';
import { useEffect, useState } from 'react';

const localUpdateEventName = (key) => `local-storage-update:${key}`;

export default function useLocalStorage(key, initValue) {
  const prefixedKey = LOCAL_STORAGE_KEY + key;

  const [value, setValue] = useState(() => {
    const jsonValue = localStorage.getItem(prefixedKey);

    if (jsonValue) return JSON.parse(jsonValue);
    if (typeof initValue === 'function') {
      return initValue();
    } else {
      return initValue;
    }
  });

  useEffect(() => {
    const serialized = JSON.stringify(value);
    // Skip the write+broadcast when the value already matches storage - for
    // object/array values, syncFromStorage below always produces a new
    // reference via JSON.parse, so without this guard every sync triggers
    // another render -> another write -> another sync, forever.
    if (localStorage.getItem(prefixedKey) === serialized) return;

    localStorage.setItem(prefixedKey, serialized);
    // The native `storage` event only fires in OTHER tabs/windows, not the
    // tab that made the change - dispatch a same-tab custom event too, so
    // multiple component instances using the same key (e.g. Header.jsx and
    // MainPage.jsx both tracking which events feed is selected) stay in
    // sync with each other, not just with other tabs.
    window.dispatchEvent(new Event(localUpdateEventName(prefixedKey)));
  }, [prefixedKey, value]);

  useEffect(() => {
    const syncFromStorage = () => {
      const jsonValue = localStorage.getItem(prefixedKey);
      if (jsonValue) {
        setValue(JSON.parse(jsonValue));
      }
    };

    window.addEventListener(localUpdateEventName(prefixedKey), syncFromStorage);
    window.addEventListener('storage', syncFromStorage);

    return () => {
      window.removeEventListener(
        localUpdateEventName(prefixedKey),
        syncFromStorage
      );
      window.removeEventListener('storage', syncFromStorage);
    };
  }, [prefixedKey]);

  return [value, setValue];
}
